import moment from 'moment';
import NewsMedia from './NewsMedia';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NewsEdit from './NewsEdit';
import MarkdownEditor from '@uiw/react-markdown-editor';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Paper,
  Typography,
  Popover,
} from '@mui/material';
import { useUser } from 'src/app/hooks/useUser';
import './NewItem.css';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import { createPortal } from 'react-dom';
import NewsItemModal from './NewsItemModal';
import LikeReaction from './Components/NewsLikeReaction';

const NewsItem = ({ post, transition, onDelete, refetchList, onEdit, handleReactionArticle }) => {
  const { t } = useTranslation('translations');
  const [anchorEl, setAnchorEl] = useState();

  const [openNews, setOpenNews] = useState(false);
  const [hover, setHover] = useState(false);
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const { isDarkMode } = useSelector((state) => state.themeSlice);
  const { isTop, isPR, user } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteArticle = () => {
    setOpenNews(false);
    setIsOpenedModal(false);
    onDelete(post);
  };

  const handleOpenNews = () => {
    setOpenNews(true);
  };

  const handleCloseNews = (_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    } else {
      setOpenNews(false);
    }
  };

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const handleEditArticle = (event) => {
    event.stopPropagation();
    setOpenEditDialog(true);
  };

  const handleShowModal = (e) => {
    e.stopPropagation();
    setIsOpenedModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenedModal(false);
  };

  return (
    <>
      <Card
        component={motion.div}
        variants={transition}
        key={post.id}
        className={`mb-16 shadow-2 `}
        sx={{ position: 'relative', minHeight: '160px' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleOpenNews}
      >
        <div className="py-10 ">
          <CardHeader
            className="absolute right-0 top-0"
            avatar={null}
            action={
              <>
                {(isTop || isPR) && (
                  <IconButton aria-label="more" size="large" onClick={handleClick} className="p-0">
                    <FuseSvgIcon>react-outline:dots-vertical</FuseSvgIcon>
                  </IconButton>
                )}
                <Menu
                  id="simple-menu"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  anchorEl={anchorEl}
                  open={!!anchorEl}
                  onClose={() => setAnchorEl(null)}
                >
                  <div className="flex flex-col">
                    <MenuItem onClick={(e) => handleShowModal(e)} className="flex justify-center">
                      {t('Delete')}
                    </MenuItem>
                    <MenuItem onClick={handleEditArticle} className="flex justify-center">
                      {t('Edit')}
                    </MenuItem>
                  </div>
                </Menu>
              </>
            }
          />
          <div className="flex flex-col md:flex-row gap-16">
            <Box
              className={`basis-1/4 rounded-8 overflow-hidden ${
                hover ? 'perspective' : ''
              }  cursor-pointer min-w-120`}
            >
              <NewsMedia
                isItem
                isPanorama={post.isPanorama}
                media={post.fileList}
                className={`transition-transform duration-500 ${hover ? 'scale-in-img' : ''}`}
              />
            </Box>
            <div
              className="basis-4/5 flex flex-col justify-between"
              onClick={(e) => e.stopPropagation()}
            >
              <CardContent className="flex flex-col pb-0" sx={{ px: 2, pb: 0 }}>
                {post.title && (
                  <Typography
                    variant="h6"
                    sx={{ cursor: 'default' }}
                    className={`
                        text-grey-800
                        dark:text-white
                        font-inter 
                        font-600
                        pt-14
                        pr-28
                        text-xl
                        transition 
                        duration-300
                        ease-in-out
                        border-b-2
                        max-w-full
                        `}
                  >
                    {post.title}
                  </Typography>
                )}
                <Typography className="text-11 text-darkgrey cursor-default">
                  {moment(post.createdDate).format('DD.MM.YYYY HH:mm')}
                </Typography>
                {post.text && (
                  <>
                    <div
                      className={`text-grey-700 dark:text-white font-inter font-500 pt-12 leading-relaxed transition-all duration-300 ease-in-out ${
                        isExpanded ? '' : 'line-clamp-3 md:line-clamp-4 lg:line-clamp-5'
                      }`}
                      style={{
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      <MarkdownEditor.Markdown
                        source={post.text}
                        style={{
                          backgroundColor: 'transparent',
                          color: isDarkMode ? '#cdcdcd' : '#444444',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    {post.text.length > 300 && (
                      <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="mt-2 text-blue-600 text-sm hover:underline self-start mb-5"
                      >
                        {isExpanded ? t('Hide') : t('Expand')}
                      </button>
                    )}
                  </>
                )}
                <LikeReaction
                  commentary
                  callback={() => setOpenNews(true)}
                  post={post}
                  refetchList={refetchList}
                  handleReactionArticle={handleReactionArticle}
                />
              </CardContent>
            </div>
          </div>
        </div>
      </Card>
      <NewsItemModal
        refetchList={refetchList}
        openNews={openNews}
        handleCloseNews={handleCloseNews}
        post={post}
        handleReactionArticle={handleReactionArticle}
      />
      {openEditDialog &&
        createPortal(
          <NewsEdit
            onOpen={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            post={post}
            refetchList={refetchList}
            onEdit={onEdit}
          />,
          document.body
        )}
      {isOpenedModal && (
        <ConfirmDeleteModalWindow
          onClose={handleCloseModal}
          modalTitle={t('AreYouSureToDeleteNews')}
          deleteMessage={handleDeleteArticle}
          open={isOpenedModal}
          setIsOpenModal={setIsOpenedModal}
        />
      )}
    </>
  );
};

export default NewsItem;
